import { ReactComponent as AddSvg } from "@/src/assets/icons/icon-add-white.svg";
import InfoSvg from "@/src/assets/icons/icon-info-gray.svg";
import { Button } from "@/src/components/atom/Button";
import Icon from "@/src/components/atom/Icon";
import { renderNoRowsComponent } from "@/src/components/atom/Table";
import ReadOnlyText from "@/src/components/atom/TextArea/ReadOnlyText";
import Typo from "@/src/components/atom/Typo";
import SectionCard from "@/src/components/molecule/SectionCard";
import AddMemoDialog from "@/src/components/organism/AddMemoDialog";
import useAlert from "@/src/hooks/useAlert";
import {
  taskDetailApi,
  useCreateTaskMemoMutation,
} from "@/src/store/apis/tasks/taskDetail";
import { SimpleTaskMemoDto } from "@/src/store/apis/tasks/taskDetail/interface";
import colorSet from "@/src/styles/color";
import { StyledScroll } from "@/src/styles/scroll";
import { isUndefined } from "@/src/utils/is";
import dayjs from "dayjs";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import styled from "styled-components";

const memoEmptyArray: SimpleTaskMemoDto[] = [];
enum DialogState {
  NULL,
  ADD_MEMO,
}

interface MemoCardProps {
  isEditAuth: boolean;
}

function MemoCard({ isEditAuth }: MemoCardProps) {
  const { t } = useTranslation();
  const params = useParams();
  const alert = useAlert();
  const [dialogState, setDialogState] = useState<DialogState>(DialogState.NULL);

  // API
  const [createMemo, { isLoading: isCreateMemoLoading }] =
    useCreateTaskMemoMutation();

  const { simpleLoadingMemos } =
    taskDetailApi.endpoints.getTaskDetail.useQueryState(
      {
        id: Number(params.id),
      },
      {
        selectFromResult: ({ currentData, isError, isFetching }) => {
          const isUnstable = isUndefined(currentData) || isFetching || isError;

          return {
            simpleLoadingMemos: !isUnstable
              ? (currentData.simpleTaskMemos ?? memoEmptyArray)
              : memoEmptyArray,
          };
        },
      }
    );

  const handleCreateMemoClick = async (memo: string) => {
    try {
      await createMemo({ id: Number(params.id), memo }).unwrap();
      setDialogState(DialogState.NULL);
    } catch (e: any) {
      const message = Array.isArray(e.data.message)
        ? e.data.message[0]
        : e.data.message;
      alert.showAlert({ type: "error", message });
    }
  };

  const renderDialog = () => {
    if (dialogState === DialogState.ADD_MEMO) {
      return (
        <AddMemoDialog
          open
          onOpenChange={() => setDialogState(DialogState.NULL)}
          onSave={handleCreateMemoClick}
          isLoading={isCreateMemoLoading}
        />
      );
    }
  };

  return (
    <StyledSectionCard
      cardTitle={t("task:detail.cards.memo")}
      rightAccessory={
        <StyledButton
          buttonSize={32}
          buttonColor="black"
          buttonGrade="tertiary"
          onClick={() => setDialogState(DialogState.ADD_MEMO)}
          disabled={!isEditAuth}
        >
          <AddIcon data-disabled={!isEditAuth} />
          {t("sales:detail.button.add")}
        </StyledButton>
      }
    >
      <MemoContainer>
        <MemoDescription>
          <StyledIcon iconSrc={InfoSvg} />
          <Typo typoType="b9r" color="gray6">
            {t("task:detail.description.memo")}
          </Typo>
        </MemoDescription>

        <MemoContent>
          {simpleLoadingMemos.length >= 1 ? (
            simpleLoadingMemos.map((item, idx) => {
              return (
                <MemoItem key={idx.toString()}>
                  <MemoLabelContainer>
                    <Typo color="gray3" typoType="h9">
                      {item.writerName}
                    </Typo>
                    <Typo color="gray7" typoType="b9r">
                      {`(${dayjs(item.wroteAt).format("YY-MM-DD")}, ${dayjs(
                        item.wroteAt
                      ).format("HH:mm")})`}
                    </Typo>
                  </MemoLabelContainer>

                  <ReadOnlyText typoType="b7r" color="gray6">
                    {item.memo}
                  </ReadOnlyText>
                </MemoItem>
              );
            })
          ) : (
            <MemoNoDataContainer>{renderNoRowsComponent()}</MemoNoDataContainer>
          )}
        </MemoContent>
      </MemoContainer>
      {renderDialog()}
    </StyledSectionCard>
  );
}

export default MemoCard;

const StyledSectionCard = styled(SectionCard)`
  grid-column: span 7;
`;

const StyledIcon = styled(Icon)`
  flex-shrink: 0;
`;

const MemoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const MemoDescription = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const MemoContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  overflow: auto;
  height: 140px;
  ${StyledScroll}
`;

const MemoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const MemoLabelContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1;
`;

const MemoNoDataContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
`;

const StyledButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const AddIcon = styled(AddSvg)`
  width: 16px;
  height: 16px;

  path {
    fill: ${colorSet.gray2};
  }

  &[data-disabled="true"] {
    path {
      fill: ${colorSet.gray8};
    }
  }
`;
